import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { ArrowRight, Broadcast } from '@phosphor-icons/react';
import { useI18n } from '../../contexts/I18nContext';
import { useOrg } from '../../contexts/OrgContext';
import { useAuth } from '../../contexts/AuthContext';
import { useRelativeTime } from '../../lib/time';
import { listGateways, fmtMac } from '../../lib/gateways';
import { GatewayStatusChip } from './GatewayStatusChip';

const COLS_SUPER = 'md:grid-cols-[1.4fr_1fr_1.1fr_0.8fr_0.8fr_0.9fr]';
const COLS_ORG = 'md:grid-cols-[1.4fr_1.1fr_0.8fr_0.8fr_auto]';

// Devices → Gateways tab: gateways of the current organisation (backend scopes it).
export function GatewayList() {
  const { t } = useI18n();
  const rel = useRelativeTime();
  const { current } = useOrg() || {};
  const { user } = useAuth();
  const isSuper = user?.is_super_admin === true;
  const COLS = isSuper ? COLS_SUPER : COLS_ORG;
  const [data, setData] = useState(null);
  const [error, setError] = useState(null);

  useEffect(() => {
    let alive = true;
    setData(null); setError(null);
    listGateways({}, current?.id)
      .then((d) => alive && setData(d))
      .catch((e) => alive && setError(e?.response?.status === 403 ? 'scope' : 'load'));
    return () => { alive = false; };
  }, [current?.id]);

  if (error) {
    return (
      <div className="bg-bg-alt rounded-2xl p-8 text-center text-ink-soft text-sm" data-testid="gateway-list-error">
        {t(error === 'scope' ? 'gw_scope_pending' : 'gw_load_failed')}
      </div>
    );
  }
  if (!data) return <div className="text-ink-muted py-10 text-center">{t('loading')}</div>;
  const list = data.gateways;

  return (
    <div className="bg-white border border-stone_line rounded-2xl shadow-soft overflow-hidden" data-testid="gateway-list">
      <div className={`hidden md:grid ${COLS} gap-4 px-6 py-3 border-b border-stone_line bg-bg-alt text-[10px] uppercase tracking-[0.18em] text-ink-muted font-semibold`}>
        <div>{t('gw_col_name')}</div>{isSuper && <div>MAC</div>}<div>{t('gw_col_room')}</div>
        <div>{t('gw_col_status')}</div><div>{t('gw_devices_seen')}</div>{isSuper ? <div>{t('gw_last_message')}</div> : <div />}
      </div>
      {list.length === 0 ? (
        <div className="p-10 text-center text-ink-muted" data-testid="gateway-list-empty">{t('gw_none_for_org')}</div>
      ) : (
        <ul className="divide-y divide-stone_line">
          {list.map((g) => (
            <li key={g.gateway_id}>
              <Link to={`/gateways/${g.gateway_id}`} data-testid={`gateway-list-row-${g.gateway_id}`}
                className={`grid grid-cols-1 ${COLS} gap-2 md:gap-4 px-6 py-4 items-center hover:bg-bg-alt/50 transition-colors ${g.deactivated ? 'opacity-60' : ''}`}>
                <div className="flex items-center gap-3 min-w-0">
                  <div className="w-9 h-9 rounded-lg bg-sage-100 text-sage-600 grid place-items-center shrink-0"><Broadcast size={18} weight="duotone" /></div>
                  <div className="min-w-0">
                    <div className="text-sm text-ink font-medium truncate">{g.gateway_name || '—'}</div>
                    <div className="text-[11px] text-ink-muted">{g.gateway_hw_type || ''}</div>
                  </div>
                </div>
                {isSuper && <div className="text-xs font-mono text-ink-soft">{fmtMac(g.gateway_mac_hex)}</div>}
                <div className="text-sm text-ink truncate">{g.room_name || <span className="text-ink-muted">{t('gw_unassigned')}</span>}</div>
                <div><GatewayStatusChip status={g.status} testid={`gateway-list-status-${g.gateway_id}`} /></div>
                <div className="text-sm text-ink tabular-nums"><span className="md:hidden text-[11px] text-ink-muted">{t('gw_devices_seen')}: </span>{g.devices_seen_latest ?? '—'}</div>
                <div className="flex items-center justify-between gap-2 text-xs text-ink-soft">
                  {isSuper && <span data-testid={`gateway-list-last-${g.gateway_id}`}>{g.last_message_at ? rel(g.last_message_at) : t('gw_never')}</span>}
                  <ArrowRight size={14} className="text-ink-muted" />
                </div>
              </Link>
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}
